import { Controller } from "@hotwired/stimulus"
import "emoji-picker-element"

export default class extends Controller {
  static targets = ["form", "showButton"]

  connect() {
    // Create the emoji picker when needed
    this.picker = null
  }

  showForm() {
    this.formTarget.classList.remove("hidden")
    this.showButtonTarget.classList.add("hidden")
    
    // Initialize the date field with today's date
    const dateField = this.formTarget.querySelector('input[name="task[sprint]"]')
    if (dateField) {
      const today = new Date()
      const year = today.getFullYear()
      const month = (today.getMonth() + 1).toString().padStart(2, '0')
      const day = today.getDate().toString().padStart(2, '0')
      dateField.value = `${year}-${month}-${day}`
      
      // Trigger the date display update
      dateField.dispatchEvent(new Event('change'))
    }
    
    // Focus the name field
    this.formTarget.querySelector('input[name="task[name]"]').focus()
  }

  showFormForUser(event) {
    event.preventDefault()
    const userId = event.currentTarget.dataset.tasksUserIdValue
    
    // Set the user_id field if it exists, or create it if it doesn't
    let userIdField = this.formTarget.querySelector('input[name="task[user_id]"]')
    if (!userIdField) {
      userIdField = document.createElement('input')
      userIdField.type = 'hidden'
      userIdField.name = 'task[user_id]'
      this.formTarget.appendChild(userIdField)
    }
    userIdField.value = userId
    
    this.showForm()
  }

  hideForm() {
    this.formTarget.classList.add("hidden")
    this.showButtonTarget.classList.remove("hidden")
    
    // Reset the form
    this.formTarget.reset()
    
    // Remove the user_id field if it exists
    const userIdField = this.formTarget.querySelector('input[name="task[user_id]"]')
    if (userIdField) {
      userIdField.remove()
    }
  }

  addTask(event) {
    event.preventDefault()
    this.formTarget.requestSubmit()
  }

  submitted(event) {
    // Check if we're in a modal context
    const modal = document.getElementById('new_task_modal')
    const fetchResponse = event.detail.fetchResponse
    
    // Check if the task was created successfully
    if (fetchResponse.response.ok) {
      // Check if the task is scheduled for a future date
      const sprintDate = this.formTarget.querySelector('input[name="task[sprint]"]').value
      const today = new Date().toISOString().split('T')[0]
      
      if (sprintDate > today) {
        // Add the toast notification
        const toast = document.createElement('div')
        toast.className = 'toast toast-end'
        toast.innerHTML = `
          <div class="alert alert-info">
            <span>As you scheduled your task in the future, it will appear on that day</span>
          </div>
        `
        document.body.appendChild(toast)
        setTimeout(() => toast.remove(), 5000)
      }
      
      if (modal) {
        modal.close()
        // Reset the form
        event.target.reset()
      } else {
        // Regular form handling for non-modal contexts
        this.hideForm()
      }
    }
  }

  handleDelete(event) {
    // Prevent the default form submission
    event.preventDefault()
    
    // Get the form from the event
    const form = event.target
    
    // Submit the form with Turbo
    form.requestSubmit()
  }

  showEmojiPicker(event) {
    const input = event.target
    
    if (!this.picker) {
      this.picker = document.createElement('emoji-picker')
      
      // Set theme based on document class
      if (document.documentElement.classList.contains('dark')) {
        this.picker.classList.add('dark')
      }
      
      // Find the closest container - try different possible parent containers
      const container = input.closest('.modal-box') || 
                       input.closest('td') || 
                       input.closest('.border-base-300') ||
                       input.closest('div')
      
      if (!container) {
        console.error('No suitable container found for emoji picker')
        return
      }
      
      // Add to document body first with invisible positioning
      this.picker.style.position = 'fixed'
      this.picker.style.visibility = 'hidden'
      document.body.appendChild(this.picker)
      
      // Wait for Shadow DOM to be initialized
      requestAnimationFrame(() => {
        // Get dimensions after Shadow DOM is ready
        const pickerHeight = this.picker.getBoundingClientRect().height
        const pickerWidth = this.picker.getBoundingClientRect().width
        
        // Calculate available space
        const rect = input.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()
        const spaceBelow = window.innerHeight - rect.bottom
        const spaceAbove = rect.top
        const spaceRight = window.innerWidth - rect.left
        
        // Remove from body and add to container with proper positioning
        document.body.removeChild(this.picker)
        this.picker.style.position = 'absolute'
        this.picker.style.visibility = 'visible'
        container.appendChild(this.picker)
        
        // Position above or below based on available space
        if (spaceBelow < pickerHeight && spaceAbove > spaceBelow) {
          // Position above if more space there
          this.picker.style.top = `${rect.top - containerRect.top - pickerHeight - 5}px`
        } else {
          // Position below
          this.picker.style.top = `${rect.bottom - containerRect.top + 5}px`
        }
        
        // Position horizontally
        if (spaceRight < pickerWidth) {
          // Align right edge with input right edge
          this.picker.style.left = `${rect.right - containerRect.left - pickerWidth}px`
        } else {
          // Align left edge with input left edge
          this.picker.style.left = `${rect.left - containerRect.left}px`
        }
      })
      
      // Handle emoji selection
      this.picker.addEventListener('emoji-click', e => {
        input.value = e.detail.unicode
        this.picker.remove()
        this.picker = null
      })
      
      // Handle click outside
      const closeHandler = (e) => {
        // Only proceed if picker still exists
        if (this.picker && !this.picker.contains(e.target) && e.target !== input) {
          this.picker.remove()
          this.picker = null
          document.removeEventListener('click', closeHandler)
        }
      }
      setTimeout(() => document.addEventListener('click', closeHandler), 0)
    } else {
      this.picker.remove()
      this.picker = null
    }
  }

  async toggleDone(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const checkbox = event.target
    const taskId = checkbox.dataset.taskId
    const userId = checkbox.dataset.userId
    const done = checkbox.checked

    try {
      const response = await fetch(`/tasks/${taskId}/toggle_done`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ done, user_id: userId })
      })

      if (!response.ok) throw new Error('Network response was not ok')
      
      // Let Turbo handle the response
      const html = await response.text()
      Turbo.renderStreamMessage(html)
    } catch (error) {
      console.error('Error:', error)
      checkbox.checked = !done // Revert checkbox state
    }
  }

  async snooze(event) {
    const taskId = event.target.dataset.taskId
    const context = event.target.dataset.context
    const userId = event.target.dataset.userId

    try {
      const response = await fetch(`/tasks/${taskId}/snooze`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ context, user_id: userId })
      })

      if (!response.ok) throw new Error('Network response was not ok')
      
      // Let Turbo handle the response
      const html = await response.text()
      Turbo.renderStreamMessage(html)
    } catch (error) {
      console.error('Error:', error)
    }
  }
} 